// Call statistics for the Monitoring page (replaces the old Statistics page).
// One /call-stats request per filter change: totals, disposition breakdown,
// timeline and tables per client / SIP / server / avatar / voice set.
//
//   params     — API filters: client_id | unassigned, sip_user, server_id,
//                template_id, from, to (see callLogFilters on the server)
//   machines   — bots in scope; SIPs with no calls in the range are listed too
//   showClients — "All clients" view: adds the per-client table
//   onPickClient(id)    — click a client row
//   onFilterSips(sips)  — "Filter to selected" in the SIP table
import { useState, useEffect, useMemo } from 'react'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts'
import { Phone, PhoneForwarded, Voicemail, PhoneOff, Clock, Cpu, Filter, ChevronUp, ChevronDown } from 'lucide-react'
import { audiosocketBotsApi } from '../services/api'
import StatCards from './StatCards'

const DISPOSITIONS = [
    { key: 'transfer-call', label: 'Transfer', color: '#22c55e' },
    { key: 'not-interested', label: 'Not Interested', color: '#f59e0b' },
    { key: 'no-answer', label: 'No Answer', color: '#3b82f6' },
    { key: 'answering-machine', label: 'Voicemail', color: '#6b7280' },
    { key: 'age-do-not-qualify', label: 'Age Do Not Qualify', color: '#ef4444' },
    { key: 'do-not-call', label: 'DNC', color: '#dc2626' },
    { key: 'call-back', label: 'Call Back', color: '#06b6d4' },
    { key: 'busy', label: 'Busy', color: '#a78bfa' },
    { key: 'in-progress', label: 'In Progress', color: '#38bdf8' },
    { key: 'unknown', label: 'Unknown', color: '#94a3b8' }
]

const fmtNum = (n) => (n || 0).toLocaleString()
const fmtDur = (s) => { s = Math.round(s || 0); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }
const pct = (n, total) => (total ? ((n / total) * 100).toFixed(1) : '0.0')
const rateClass = (r) => (r >= 5 ? 'tw-text-green-500' : r >= 2 ? 'tw-text-amber-500' : 'tw-text-red-500')
function fmtWhen(s) {
    if (!s) return '—'
    const d = new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(s) ? s : String(s).replace(' ', 'T') + 'Z')
    return isNaN(d) ? s : d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function CallStatsPanel({ params, machines = [], showClients = false, onPickClient, onFilterSips }) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [err, setErr] = useState(null)
    const key = JSON.stringify(params)

    useEffect(() => {
        let cancelled = false
        setLoading(true); setErr(null)
        audiosocketBotsApi.callStats(params)
            .then(r => { if (!cancelled) { if (!r.data.success) throw new Error(r.data.error || 'failed'); setData(r.data) } })
            .catch(e => { if (!cancelled) setErr(e.response?.data?.error || e.message) })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key])

    // Every bot in scope, with its call numbers (0 when it took no calls).
    const sipRows = useMemo(() => {
        const bySip = new Map((data?.by_sip || []).filter(r => r.sip_user).map(r => [r.sip_user, r]))
        const rows = (data?.by_sip || []).filter(r => r.sip_user).map(r => {
            const m = machines.find(x => x.sip_user === r.sip_user)
            return { ...r, server_name: m?.server_name || r.server_name, status: m?.status, template_name: m?.template_name }
        })
        for (const m of machines) {
            if (m.sip_user && !bySip.has(m.sip_user)) {
                rows.push({ sip_user: m.sip_user, server_name: m.server_name, status: m.status, template_name: m.template_name, total: 0, transfers: 0, voicemail: 0, not_interested: 0, avg_duration: 0, transfer_rate: 0, last_call_at: null })
            }
        }
        return rows
    }, [data, machines])

    if (loading && !data) return <div className="tw-p-6 tw-text-sm tw-text-muted">Loading stats…</div>
    if (err) return <div className="tw-p-6 tw-text-sm tw-text-red-500">{err}</div>
    if (!data) return null

    const t = data.totals
    const silentBots = sipRows.filter(r => r.total === 0).length
    const dispoRows = DISPOSITIONS.map(d => ({ ...d, count: data.disposition_counts[d.key] || 0 })).filter(r => r.count > 0).sort((a, b) => b.count - a.count)
    const maxDispo = Math.max(1, ...dispoRows.map(r => r.count))
    const timeline = data.timeline.points.map(p => {
        const d = new Date(data.timeline.unit === 'hour' ? p.bucket : `${p.bucket}T00:00:00`)
        return {
            ...p,
            label: data.timeline.unit === 'hour'
                ? d.toLocaleTimeString(undefined, { hour: 'numeric' })
                : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        }
    })

    return (
        <div className={`tw-flex tw-flex-col tw-gap-4 ${loading ? 'tw-opacity-60' : ''}`}>
            <StatCards cards={[
                { key: 'calls', label: 'Total calls', value: fmtNum(t.total), icon: Phone, color: '#3b82f6', sub: `${fmtNum(t.talk_time ? Math.round(t.talk_time / 3600) : 0)} h talk time` },
                { key: 'xfer', label: 'Transfers', value: fmtNum(t.transfers), icon: PhoneForwarded, color: '#22c55e', sub: `${t.transfer_rate}% transfer rate` },
                { key: 'vm', label: 'Voicemail', value: fmtNum(t.voicemail), icon: Voicemail, color: '#6b7280', sub: `${pct(t.voicemail, t.total)}% of calls` },
                { key: 'ni', label: 'Not interested', value: fmtNum(t.not_interested), icon: PhoneOff, color: '#f59e0b', sub: `${pct(t.not_interested, t.total)}% of calls` },
                { key: 'dur', label: 'Avg duration', value: fmtDur(t.avg_duration), icon: Clock, color: '#8b5cf6', sub: `${t.avg_steps} steps on average` },
                {
                    key: 'bots', label: 'Bots calling', value: fmtNum(sipRows.length - silentBots), icon: Cpu, color: silentBots ? '#ef4444' : '#06b6d4',
                    sub: silentBots ? `${silentBots} of ${sipRows.length} took no calls` : `all ${sipRows.length} took calls`
                }
            ]} />

            <div className="tw-grid tw-grid-cols-1 tw-gap-4 xl:tw-grid-cols-[2fr_1fr]">
                <Card title={`Calls per ${data.timeline.unit}`}>
                    {timeline.length === 0 ? <Empty /> : (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={timeline} margin={{ left: -10, right: 8, top: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.1} vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                                <Tooltip cursor={{ fill: 'rgba(148,163,184,0.08)' }} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12 }} />
                                <Bar dataKey="total" name="Calls" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="transfers" name="Transfers" fill="#22c55e" radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Card>
                <Card title="Dispositions">
                    {dispoRows.length === 0 ? <Empty /> : dispoRows.map(r => (
                        <div key={r.key} className="tw-mb-2.5">
                            <div className="tw-mb-1 tw-flex tw-justify-between tw-text-xs">
                                <span className="tw-font-medium" style={{ color: r.color }}>{r.label}</span>
                                <span className="tw-tabular-nums tw-text-muted">{fmtNum(r.count)} · {pct(r.count, t.total)}%</span>
                            </div>
                            <div className="tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-input">
                                <div className="tw-h-full tw-rounded-full" style={{ width: `${(r.count / maxDispo) * 100}%`, background: r.color }} />
                            </div>
                        </div>
                    ))}
                </Card>
            </div>

            {showClients && (
                <StatsTable title="Clients" rows={data.by_client} rowKey={r => r.client_id ?? 'none'}
                    onRowClick={onPickClient ? (r => onPickClient(r.client_id)) : null}
                    columns={[
                        { key: 'client_name', label: 'Client', render: r => r.client_name || (r.client_id ? `Client #${r.client_id}` : 'Unassigned'), strong: true },
                        { key: 'sips', label: 'SIPs', num: true },
                        ...METRIC_COLS
                    ]} />
            )}

            <SipTable rows={sipRows} onFilterSips={onFilterSips} />

            <StatsTable title="Servers" rows={data.by_server} rowKey={r => r.server_id ?? 'none'}
                columns={[
                    { key: 'server_name', label: 'Server', render: r => r.server_name || (r.server_id ? `#${r.server_id}` : '—'), strong: true },
                    { key: 'host_ip', label: 'IP', mono: true },
                    { key: 'sips', label: 'SIPs', num: true },
                    ...METRIC_COLS
                ]} />

            <div className="tw-grid tw-grid-cols-1 tw-gap-4 xl:tw-grid-cols-2">
                <StatsTable title="Avatars" rows={data.by_avatar} rowKey={r => r.avatar_name ?? 'none'} compact
                    columns={[{ key: 'avatar_name', label: 'Avatar', render: r => r.avatar_name || '—', strong: true }, ...METRIC_COLS.slice(0, 3), METRIC_COLS[5]]} />
                <StatsTable title="Voice sets" rows={data.by_voice_set} rowKey={r => r.voice_set_name ?? 'none'} compact
                    columns={[{ key: 'voice_set_name', label: 'Voice set', render: r => r.voice_set_name || '—', strong: true }, ...METRIC_COLS.slice(0, 3), METRIC_COLS[5]]} />
            </div>
        </div>
    )
}

const METRIC_COLS = [
    { key: 'total', label: 'Calls', num: true, render: r => fmtNum(r.total) },
    { key: 'transfers', label: 'Xfers', num: true, render: r => fmtNum(r.transfers) },
    { key: 'transfer_rate', label: 'Rate', num: true, render: r => <span className={`tw-font-semibold ${r.total ? rateClass(r.transfer_rate) : 'tw-text-muted'}`}>{r.transfer_rate}%</span> },
    { key: 'voicemail', label: 'Voicemail', num: true, render: r => fmtNum(r.voicemail) },
    { key: 'not_interested', label: 'Not int.', num: true, render: r => fmtNum(r.not_interested) },
    { key: 'avg_duration', label: 'Avg dur', num: true, render: r => fmtDur(r.avg_duration) },
    { key: 'last_call_at', label: 'Last call', render: r => <span className="tw-text-muted">{fmtWhen(r.last_call_at)}</span> }
]

function Card({ title, children, right }) {
    return (
        <div className="tw-rounded-[10px] tw-border tw-border-solid tw-border-border tw-bg-card tw-p-4">
            <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-2">
                <h4 className="tw-m-0 tw-text-[13px] tw-font-semibold">{title}</h4>
                {right}
            </div>
            {children}
        </div>
    )
}
const Empty = () => <div className="tw-py-6 tw-text-center tw-text-xs tw-text-muted">No calls in this range.</div>

function useSort(rows, initial = 'total') {
    const [sort, setSort] = useState({ key: initial, dir: -1 })
    const sorted = useMemo(() => [...rows].sort((a, b) => {
        const x = a[sort.key], y = b[sort.key]
        if (x == null && y == null) return 0
        if (x == null) return 1
        if (y == null) return -1
        return (x < y ? -1 : x > y ? 1 : 0) * sort.dir
    }), [rows, sort])
    const header = (c) => (
        <th key={c.key} onClick={() => setSort(s => ({ key: c.key, dir: s.key === c.key ? -s.dir : (c.num ? -1 : 1) }))}
            className={`tw-cursor-pointer tw-select-none tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted ${c.num ? 'tw-text-right' : 'tw-text-left'}`}>
            {c.label}
            {sort.key === c.key && (sort.dir < 0 ? <ChevronDown size={11} className="tw-ml-0.5 tw-inline" /> : <ChevronUp size={11} className="tw-ml-0.5 tw-inline" />)}
        </th>
    )
    return { sorted, header }
}

function cell(c, r) {
    const v = c.render ? c.render(r) : (r[c.key] ?? '—')
    return (
        <td key={c.key} className={`tw-whitespace-nowrap tw-px-3 tw-py-1.5 ${c.num ? 'tw-text-right tw-tabular-nums' : ''} ${c.mono ? 'tw-font-mono' : ''} ${c.strong ? 'tw-font-semibold' : ''}`}>{v}</td>
    )
}

function StatsTable({ title, rows, columns, rowKey, onRowClick, compact }) {
    const [all, setAll] = useState(false)
    const { sorted, header } = useSort(rows)
    const limit = compact ? 8 : 15
    const shown = all ? sorted : sorted.slice(0, limit)
    return (
        <Card title={<>{title} <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-muted">{rows.length}</span></>}
            right={rows.length > limit && (
                <button className="btn btn-secondary btn-sm" onClick={() => setAll(a => !a)}>{all ? `Top ${limit}` : `Show all ${rows.length}`}</button>
            )}>
            {rows.length === 0 ? <Empty /> : (
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-border-collapse tw-text-xs">
                        <thead><tr className="tw-border-0 tw-border-b tw-border-solid tw-border-border">{columns.map(header)}</tr></thead>
                        <tbody>
                            {shown.map(r => (
                                <tr key={rowKey(r)} onClick={onRowClick ? () => onRowClick(r) : undefined}
                                    className={`tw-border-0 tw-border-t tw-border-solid tw-border-border ${onRowClick ? 'tw-cursor-pointer hover:tw-bg-card-hover' : ''}`}>
                                    {columns.map(c => cell(c, r))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    )
}

// Per-SIP table with checkboxes: pick a group of bots and filter the whole
// page (stats, call logs, bots, VMs) down to them.
function SipTable({ rows, onFilterSips }) {
    const [picked, setPicked] = useState(() => new Set())
    const [all, setAll] = useState(false)
    const { sorted, header } = useSort(rows)
    const shown = all ? sorted : sorted.slice(0, 25)
    const toggle = (s) => setPicked(p => { const n = new Set(p); n.has(s) ? n.delete(s) : n.add(s); return n })
    const columns = [
        { key: 'sip_user', label: 'SIP', mono: true, strong: true },
        { key: 'server_name', label: 'Server', render: r => r.server_name || '—' },
        { key: 'template_name', label: 'Template', render: r => r.template_name || '—' },
        ...METRIC_COLS
    ]
    return (
        <Card title={<>Bots (SIPs) <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-muted">{rows.length}</span></>}
            right={<div className="tw-flex tw-items-center tw-gap-2">
                {onFilterSips && picked.size > 0 && (
                    <button className="btn btn-primary btn-sm" onClick={() => { onFilterSips([...picked]); setPicked(new Set()) }}>
                        <Filter size={12} /> Filter to {picked.size} selected
                    </button>
                )}
                {rows.length > 25 && <button className="btn btn-secondary btn-sm" onClick={() => setAll(a => !a)}>{all ? 'Top 25' : `Show all ${rows.length}`}</button>}
            </div>}>
            {rows.length === 0 ? <Empty /> : (
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-border-collapse tw-text-xs">
                        <thead><tr className="tw-border-0 tw-border-b tw-border-solid tw-border-border">
                            {onFilterSips && <th className="tw-w-8 tw-px-3 tw-py-2" />}
                            {columns.map(header)}
                        </tr></thead>
                        <tbody>
                            {shown.map(r => (
                                <tr key={r.sip_user} onClick={onFilterSips ? () => toggle(r.sip_user) : undefined}
                                    className={`tw-border-0 tw-border-t tw-border-solid tw-border-border ${onFilterSips ? 'tw-cursor-pointer hover:tw-bg-card-hover' : ''} ${r.total === 0 ? 'tw-opacity-60' : ''}`}>
                                    {onFilterSips && <td className="tw-px-3 tw-py-1.5"><input type="checkbox" readOnly checked={picked.has(r.sip_user)} /></td>}
                                    {columns.map(c => cell(c, r))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    )
}
